/**
 * O vínculo da pessoa com a conta dela no GitLab (D7, spec
 * 2026-09-24-runner-confirmar-e-mergear). Com ele, o Confirmar do ajuste de
 * infraestrutura mergeia o MR com a identidade dela — o GitLab mostra quem
 * decidiu, não a conta de serviço.
 */
import { useEffect, useRef, useState } from 'react'
import { Badge, Box, Button, Group, Loader, Stack, Text, ThemeIcon } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconBrandGitlab } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CHAVE_VINCULO_GITLAB, conectarGitlab, desvincularGitlab, getVinculoGitlab,
} from '@/api/gitlabIdentidade'
import { formatApiError } from '@/api/errors'
import { WidgetCard } from '@/components/ui'
import { FONTE_DADO } from '@/pages/chat/components/Dado'
import { ehResultado } from './gitlabJanela'

const JANELA = 'popup,width=640,height=760'

function descricao(configurado: boolean, vinculado: boolean, revogado: boolean): string {
  if (!configurado) return 'O aplicativo do GitLab ainda não foi configurado nesta instalação.'
  if (vinculado) return 'O Confirmar do ajuste de infraestrutura mergeia o MR em seu nome.'
  if (revogado) {
    return 'O ConsoleAI perdeu o acesso à sua conta do GitLab. Conecte de novo para confirmar ajustes pelo chat.'
  }
  return 'Conecte sua conta para que o Confirmar do ajuste de infraestrutura mergeie o MR em seu nome.'
}

export function GitlabVinculoCard() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: [CHAVE_VINCULO_GITLAB], queryFn: getVinculoGitlab })
  // A autorização abre numa janela à parte: o Perfil fica, e o botão acompanha.
  const [aguardando, setAguardando] = useState(false)
  const janela = useRef<Window | null>(null)
  const atualizar = () => qc.invalidateQueries({ queryKey: [CHAVE_VINCULO_GITLAB] })

  useEffect(() => {
    if (!aguardando) return
    const encerrar = () => { setAguardando(false); janela.current = null }
    const aoReceber = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin || !ehResultado(ev.data)) return
      encerrar()
      atualizar()
      if (ev.data.ok) {
        notifications.show({ color: 'teal', title: 'GitLab conectado',
                             message: `Conectado como ${ev.data.gitlab_username}.` })
      } else {
        notifications.show({ color: 'red', title: 'GitLab não conectado', message: ev.data.motivo })
      }
    }
    // Fechar a janela sem autorizar não manda mensagem nenhuma: o botão volta sozinho.
    const vigia = window.setInterval(() => {
      if (janela.current?.closed) { encerrar(); atualizar() }
    }, 500)
    window.addEventListener('message', aoReceber)
    return () => { window.removeEventListener('message', aoReceber); window.clearInterval(vigia) }
  }, [aguardando])  // eslint-disable-line react-hooks/exhaustive-deps

  const conectar = () => {
    if (aguardando) { janela.current?.focus(); return }
    // Aberta no próprio clique: depois de esperar o servidor, o navegador bloquearia a janela.
    const j = window.open('', 'consoleai-gitlab', JANELA)
    janela.current = j
    setAguardando(true)
    conectarGitlab()
      .then(({ url }) => {
        if (j && !j.closed) j.location.href = url
        else window.location.assign(url)  // janela bloqueada: segue na página inteira
      })
      .catch((err) => {
        j?.close()
        setAguardando(false)
        notifications.show({ color: 'red', title: 'Não foi possível conectar ao GitLab',
                             message: formatApiError(err) })
      })
  }
  const desconectar = useMutation({
    mutationFn: desvincularGitlab,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CHAVE_VINCULO_GITLAB] }),
    onError: (err) => {
      notifications.show({ color: 'red', title: 'Não foi possível desconectar', message: formatApiError(err) })
    },
  })
  if (!data) return null
  const { configurado, vinculado, revogado } = data
  return (
    <WidgetCard p="md">
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size="xl" radius="md" variant="light" color={vinculado ? 'teal' : 'gray'}>
            <IconBrandGitlab />
          </ThemeIcon>
          <Box style={{ flex: 1 }}>
            <Text fw={600}>Conta do GitLab</Text>
            <Text size="sm" c="dimmed">{descricao(configurado, vinculado, revogado)}</Text>
          </Box>
          {configurado && (
            <Badge ml="auto" color={vinculado ? 'teal' : 'gray'}>
              {vinculado ? 'Conectada' : 'Não conectada'}
            </Badge>
          )}
        </Group>
        {configurado && vinculado && (
          <Group gap="xs">
            <Text size="sm">Conectado como</Text>
            <Text size="sm" style={{ fontFamily: FONTE_DADO }}>{data.gitlab_username}</Text>
            <Button size="xs" variant="light" color="red"
                    loading={desconectar.isPending} onClick={() => desconectar.mutate()}>
              Desconectar
            </Button>
          </Group>
        )}
        {configurado && !vinculado && (
          <Stack gap={4}>
            <Group>
              <Button leftSection={aguardando ? <Loader size={14} color="white" /> : <IconBrandGitlab size={16} />}
                      onClick={conectar}>
                {aguardando ? 'Conectando…' : 'Conectar GitLab'}
              </Button>
            </Group>
            {aguardando && (
              <Text size="xs" c="dimmed">Autorize o ConsoleAI na janela do GitLab que abriu.</Text>
            )}
          </Stack>
        )}
      </Stack>
    </WidgetCard>
  )
}
